import { FISH_LAYERS } from '@/fish/lib/enums'
import type MapFeature from '@/lib/map-feature'

/**
 * Is a feature a waterbody
 * @param feature - feature from map tiles
 */
export function isWaterbody(feature: MapFeature) {
  return feature?.sourceLayer === FISH_LAYERS.WATERBODY_SOURCE
}

/**
 * Is a feature a fishing region
 * @param feature - feature from map tiles
 */
export function isFishingRegion(feature: MapFeature) {
  return feature?.sourceLayer === FISH_LAYERS.REGION
}

/**
 * Is a feature a water access site
 * @param feature - feature from map tiles
 */
export function isWaterAccessSite(feature: MapFeature) {
  return feature?.sourceLayer === FISH_LAYERS.WATER_ACCESS_SOURCE_LAYER
}

/**
 * Is a feature a badge located on top of a region
 * @param feature - feature from map tiles
 */
export function isRegionBadge(feature: MapFeature) {
  return (
    feature?.sourceLayer === FISH_LAYERS.REGION_LABEL &&
    feature.source === FISH_LAYERS.WATER_ACCESS_SOURCE
  )
}

/**
 * Is a feature a badge located on top of a waterbody
 * @param feature - feature from map tiles
 */
export function isWaterbodyBadge(feature: MapFeature) {
  return (
    (feature?.sourceLayer === FISH_LAYERS.POI_SOURCE &&
      feature.source === FISH_LAYERS.WATERBODY_LAYER) ||
    isRegionBadge(feature)
  )
}

/**
 * Is a feature a fishing area
 * @param feature - feature from map tiles
 */
export function isFishingArea(feature: MapFeature) {
  return feature?.sourceLayer === FISH_LAYERS.FISHING_AREA_SOURCE_LAYER
}

/**
 * Is an event badge on top of a waterbody
 * @param feature - feature from map tiles
 */
export function isEventClick(feature: MapFeature) {
  return feature?.sourceLayer === FISH_LAYERS.EVENTS
}

/**
 * Is a catch polygon
 * @param feature - feature from map tiles
 */
export function isCatchPolygon(feature: MapFeature) {
  return feature?.sourceLayer === FISH_LAYERS.CATCH_POLYGON
}

/**
 * Parsing function to return an array of identifiers from a fish feature.
 * Expects the feature's `data:id` property to be a stringified JSON value,
 * typically a JSON array (or single value) of identifier URNs, e.g.:
 * "['urn:onx:was.dnr.in.gov:abc123', 'urn:onx:was.dnr.in.gov:def456']".
 * @param feature - feature from map tiles
 */
export const parseFeatureIdentifier = (feature: MapFeature): string[] => {
  // The identifiers are stored in the tiles like this:
  // "['urn:onx:was.dnr.in.gov:abc123', 'urn:onx:was.dnr.in.gov:def456']"
  // We need to parse this string to an Array type to use as a filter
  const identifier = feature.properties['data:id']
  if (!identifier || typeof identifier !== 'string') {
    return []
  }

  try {
    const identifierArray = JSON.parse(identifier.replace(/'/g, '"'))
    return Array.isArray(identifierArray) ? identifierArray : [identifierArray]
  } catch (e) {
    return []
  }
}
